<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import Swal from "sweetalert2";
import BankSection from "./company/BankSection.vue";
import BusinessSection from "./company/BusinessSection.vue";
import CompanyProfileHeader from "./company/CompanyProfileHeader.vue";
import GstSection from "./company/GstSection.vue";
import InvoiceSection from "./company/InvoiceSection.vue";
import LocationsSection from "./company/LocationsSection.vue";
import { SECTIONS, sectionOfField } from "./company/companySettingsRules";
import { provideCompanySettings } from "./company/useCompanySettings";

const store = useStore();
const route = useRoute();
const router = useRouter();
const settings = provideCompanySettings(store);
const { loading, saving, canEdit, shownErrors, errorCounts, isDirty } = settings;

const SECTION_COMPONENTS = {
  business: BusinessSection,
  gst: GstSection,
  bank: BankSection,
  invoice: InvoiceSection,
  locations: LocationsSection,
};

const validTab = (key) => (SECTIONS.some((section) => section.key === key) ? key : "business");
const activeTab = ref(validTab(route.query.tab));
const loadFailed = ref(false);
const narrow = ref(false);
const errorTotal = computed(() => Object.keys(shownErrors.value).length);

function onTabChange(key) {
  router.replace({ query: { ...route.query, tab: key } });
}

/** Switch to a tab and focus one of its fields. */
async function goTo(section, field) {
  activeTab.value = section;
  onTabChange(section);
  if (!field) return;
  await nextTick();
  const element = document.getElementById(`cs-${field}`);
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    element.focus({ preventScroll: true });
  }
}

async function save() {
  if (!canEdit.value || saving.value) return;
  try {
    const result = await settings.save();
    if (!result.ok) {
      const firstField = Object.keys(result.errors)[0];
      await goTo(sectionOfField(firstField) || "business", firstField);
      return;
    }
    Swal.fire({ icon: "success", title: "Saved", text: "Company settings updated.", timer: 1600, showConfirmButton: false });
  } catch (error) {
    const detail = error?.response?.data?.detail;
    const message = Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : detail;
    Swal.fire("Could not save", message || "Something went wrong while saving.", "error");
  }
}

async function discard() {
  const result = await Swal.fire({
    title: "Discard your changes?",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Discard",
    confirmButtonColor: "#dc3545",
  });
  if (result.isConfirmed) settings.discard();
}

async function load() {
  loadFailed.value = false;
  try {
    await settings.load();
  } catch {
    loadFailed.value = true;
  }
}

// Unsaved changes: ask before leaving the page, closing the tab, or reloading.
onBeforeRouteLeave(async () => {
  if (!isDirty.value) return true;
  const result = await Swal.fire({
    title: "Leave without saving?",
    text: "Your changes to company settings will be lost.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Leave",
    cancelButtonText: "Stay",
  });
  return result.isConfirmed;
});

function onBeforeUnload(event) {
  if (isDirty.value) {
    event.preventDefault();
    event.returnValue = "";
  }
}

function onKeydown(event) {
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
    event.preventDefault();
    if (isDirty.value) save();
  }
}

let mediaQuery = null;
const onMediaChange = () => (narrow.value = !!mediaQuery?.matches);

onMounted(() => {
  load();
  window.addEventListener("beforeunload", onBeforeUnload);
  window.addEventListener("keydown", onKeydown);
  mediaQuery = window.matchMedia("(max-width: 991px)");
  onMediaChange();
  mediaQuery.addEventListener?.("change", onMediaChange);
});

onBeforeUnmount(() => {
  window.removeEventListener("beforeunload", onBeforeUnload);
  window.removeEventListener("keydown", onKeydown);
  mediaQuery?.removeEventListener?.("change", onMediaChange);
});
</script>

<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content company-settings">
        <pageaddheader title="Company Settings" title1="Your business details, GST, bank, invoice and locations" :items="[]" />

        <div v-if="loading" class="card">
          <div class="card-body">
            <a-skeleton avatar active :paragraph="{ rows: 2 }" />
            <a-skeleton active :paragraph="{ rows: 6 }" class="mt-4" />
          </div>
        </div>

        <div v-else-if="loadFailed" class="card">
          <div class="card-body">
            <a-result status="warning" title="Company settings could not be loaded" sub-title="Check your connection and try again.">
              <template #extra>
                <button type="button" class="btn btn-primary" @click="load">Try again</button>
              </template>
            </a-result>
          </div>
        </div>

        <template v-else>
          <a-alert
            v-if="!canEdit"
            type="info"
            show-icon
            class="mb-3"
            message="You can view these settings. Ask an administrator to change them."
          />

          <div class="card cs-card">
            <div class="card-body">
              <CompanyProfileHeader @go="goTo" />
            </div>
          </div>

          <div class="card cs-card">
            <div class="card-body p-0">
              <a-tabs
                v-model:activeKey="activeTab"
                :tab-position="narrow ? 'top' : 'left'"
                class="cs-tabs"
                @change="onTabChange"
              >
                <a-tab-pane v-for="section in SECTIONS" :key="section.key">
                  <template #tab>
                    <span class="cs-tab-label">
                      <vue-feather :type="section.icon" size="16"></vue-feather>
                      {{ section.label }}
                      <span v-if="errorCounts[section.key]" class="cs-tab-errors">{{ errorCounts[section.key] }}</span>
                    </span>
                  </template>
                  <component :is="SECTION_COMPONENTS[section.key]" />
                </a-tab-pane>
              </a-tabs>
            </div>
          </div>

          <transition name="cs-slide">
            <div v-if="canEdit && isDirty" class="cs-savebar" role="region" aria-label="Unsaved changes">
              <div class="cs-savebar-text">
                <vue-feather type="edit-3" size="16"></vue-feather>
                <span>You have unsaved changes</span>
                <span v-if="errorTotal" class="cs-savebar-errors">
                  · {{ errorTotal }} field{{ errorTotal === 1 ? "" : "s" }} to fix
                </span>
              </div>
              <div class="cs-savebar-actions">
                <button type="button" class="btn btn-outline-secondary" :disabled="saving" @click="discard">Discard</button>
                <button type="button" class="btn btn-primary" :disabled="saving" @click="save">
                  {{ saving ? "Saving…" : "Save changes" }}
                </button>
              </div>
            </div>
          </transition>
        </template>
      </div>
    </div>
  </div>
</template>

<style>
/* Shared by the company settings section components (all prefixed with cs-). */
.company-settings {
  --cs-accent: #fb923c;
  --cs-accent-dark: #ea7a1f;
  --cs-accent-soft: #fff4ea;
  --cs-border: #e5e7eb;
  --cs-text: #1f2937;
  --cs-muted: #6b7280;
  --cs-danger: #dc2626;
  --cs-success: #16a34a;
  padding-bottom: 96px;
}
.company-settings .cs-card {
  border: 1px solid var(--cs-border);
  border-radius: 12px;
  box-shadow: none;
  margin-bottom: 16px;
}
.company-settings .btn-primary {
  background: var(--cs-accent);
  border-color: var(--cs-accent);
  color: #fff;
}
.company-settings .btn-link {
  color: var(--cs-accent-dark);
  text-decoration: none;
  font-weight: 500;
}
.company-settings .btn-link:hover {
  text-decoration: underline;
}
.company-settings .btn-link.text-danger {
  color: var(--cs-danger);
}
.company-settings .btn-primary:hover:not(:disabled) {
  background: var(--cs-accent-dark);
  border-color: var(--cs-accent-dark);
}

/* Header */
.cs-hero {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 20px;
  align-items: center;
}
.cs-hero-main {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}
.cs-logo-button {
  position: relative;
  width: 76px;
  height: 76px;
  border-radius: 14px;
  border: 1px solid var(--cs-border);
  background: var(--cs-accent-soft);
  padding: 0;
  overflow: hidden;
  flex-shrink: 0;
}
.cs-logo-button img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #fff;
}
.cs-logo-initials {
  font-size: 26px;
  font-weight: 700;
  color: var(--cs-accent-dark);
}
.cs-logo-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(17, 24, 39, 0.45);
  color: #fff;
  opacity: 0;
  transition: opacity 0.15s;
}
.cs-logo.editable .cs-logo-button:hover .cs-logo-overlay,
.cs-logo.busy .cs-logo-overlay {
  opacity: 1;
}
.cs-hero-text h4 {
  margin: 0 0 4px;
  font-weight: 700;
  color: var(--cs-text);
  word-break: break-word;
}
.cs-hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  color: var(--cs-muted);
  font-size: 13px;
  align-items: center;
}
.cs-badge {
  background: var(--cs-accent-soft);
  color: var(--cs-accent-dark);
  border-radius: 999px;
  padding: 2px 10px;
  font-weight: 600;
}
.cs-logo-actions {
  margin-top: 6px;
  font-size: 13px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
.cs-progress {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fafafa;
  border: 1px solid var(--cs-border);
  border-radius: 12px;
  padding: 12px 16px;
}
.cs-checklist {
  margin: 4px 0 0;
  padding-left: 16px;
  font-size: 13px;
}

/* Tabs */
.cs-tabs .ant-tabs-nav {
  min-width: 230px;
  padding: 12px 0;
  background: #fafafa;
  border-radius: 12px 0 0 12px;
}
.cs-tabs.ant-tabs-top .ant-tabs-nav {
  padding: 0 12px;
  border-radius: 12px 12px 0 0;
}
.cs-tabs .ant-tabs-tab {
  padding: 10px 20px !important;
}
.cs-tabs .ant-tabs-tab-active .ant-tabs-tab-btn,
.cs-tabs .ant-tabs-tab:hover {
  color: var(--cs-accent-dark) !important;
}
.cs-tabs .ant-tabs-ink-bar {
  background: var(--cs-accent) !important;
}
.cs-tabs .ant-tabs-content-holder {
  padding: 20px 24px;
  min-width: 0;
}
.cs-tab-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.cs-tab-errors {
  background: var(--cs-danger);
  color: #fff;
  border-radius: 999px;
  font-size: 11px;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  padding: 0 5px;
}

/* Sections and fields */
.cs-group + .cs-group {
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid var(--cs-border);
}
.cs-group-head {
  margin-bottom: 14px;
}
.cs-group-head h5 {
  margin: 0;
  font-weight: 600;
  color: var(--cs-text);
}
.cs-group-head p {
  margin: 2px 0 0;
  color: var(--cs-muted);
  font-size: 13px;
}
.cs-field {
  margin-bottom: 4px;
}
.cs-field + .cs-field {
  margin-top: 14px;
}
.cs-label {
  display: block;
  font-weight: 500;
  color: var(--cs-text);
  margin-bottom: 6px;
}
.cs-required {
  color: var(--cs-danger);
  margin-left: 2px;
}
.cs-optional {
  color: #9ca3af;
  font-size: 12px;
  font-weight: 400;
  margin-left: 6px;
}
.company-settings .form-control,
.company-settings .form-select {
  border-radius: 8px;
  border-color: #d1d5db;
  min-height: 40px;
}
.company-settings .form-control:focus,
.company-settings .form-select:focus {
  border-color: var(--cs-accent);
  box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.15);
}
.company-settings .cs-field.has-error .form-control,
.company-settings .cs-field.has-error .form-select {
  border-color: var(--cs-danger);
}
.cs-error {
  color: var(--cs-danger);
  font-size: 12.5px;
  margin-top: 5px;
}
.cs-hint {
  color: var(--cs-muted);
  font-size: 12.5px;
  margin-top: 5px;
}
.cs-detected {
  color: var(--cs-success);
  font-size: 12.5px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.cs-note {
  color: var(--cs-muted);
  font-size: 13px;
  margin: 12px 0 0;
  display: flex;
  align-items: center;
  gap: 6px;
}
.cs-number-preview {
  font-size: 12.5px;
  color: var(--cs-muted);
  margin-top: 6px;
}
.cs-number-preview code {
  color: var(--cs-accent-dark);
  background: var(--cs-accent-soft);
  padding: 1px 6px;
  border-radius: 4px;
}
.cs-choice-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
}
.cs-choice {
  display: flex;
  gap: 10px;
  text-align: left;
  border: 1.5px solid var(--cs-border);
  border-radius: 10px;
  background: #fff;
  padding: 12px 14px;
  transition: border-color 0.15s, background 0.15s;
}
.cs-choice strong {
  display: block;
  color: var(--cs-text);
}
.cs-choice small {
  color: var(--cs-muted);
}
.cs-choice:hover:not(:disabled) {
  border-color: var(--cs-accent);
}
.cs-choice.active {
  border-color: var(--cs-accent);
  background: var(--cs-accent-soft);
}
.cs-choice-dot {
  width: 16px;
  height: 16px;
  margin-top: 3px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  flex-shrink: 0;
}
.cs-choice.active .cs-choice-dot {
  border: 5px solid var(--cs-accent);
}

/* Previews */
.cs-preview-card,
.cs-invoice-preview {
  margin-top: 24px;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  padding: 14px 16px;
  background: #fcfcfd;
  font-size: 13px;
}
.cs-preview-title {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9ca3af;
  margin-bottom: 8px;
}
.cs-invoice-head {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  border-bottom: 1px solid var(--cs-border);
  padding-bottom: 10px;
}
.cs-invoice-head img {
  width: 48px;
  height: 48px;
  object-fit: contain;
}
.cs-invoice-no {
  margin-left: auto;
  text-align: right;
}
.cs-invoice-body {
  color: #cbd5e1;
  padding: 16px 0;
  text-align: center;
}
.cs-invoice-foot {
  border-top: 1px solid var(--cs-border);
  padding-top: 10px;
  white-space: pre-line;
}

/* Locations */
.cs-usage {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: var(--cs-muted);
  margin-bottom: 12px;
  max-width: 420px;
}
.cs-usage .ant-progress {
  flex: 1;
  margin: 0;
}

/* Ant Design controls in the app accent (the modal is teleported to <body>, so no variables). */
.company-settings .ant-switch-checked,
.cs-modal .ant-switch-checked {
  background: #fb923c;
}
.cs-modal .ant-btn-primary {
  background: #fb923c;
  border-color: #fb923c;
}
.cs-modal .ant-btn-primary:hover,
.cs-modal .ant-btn-primary:focus {
  background: #ea7a1f;
  border-color: #ea7a1f;
}
.cs-modal .ant-btn-default:hover {
  color: #ea7a1f;
  border-color: #fb923c;
}
.cs-modal .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
  background: #fb923c;
  border-color: #fb923c;
  color: #fff;
}
.cs-modal .ant-radio-button-wrapper:hover {
  color: #ea7a1f;
}
.cs-modal .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled):hover {
  color: #fff;
}
.cs-modal .form-control:focus {
  border-color: #fb923c;
  box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.15);
}
.cs-modal .cs-label {
  display: block;
  font-weight: 500;
  margin-bottom: 6px;
}
.cs-modal .cs-field + .cs-field {
  margin-top: 14px;
}

/* Sticky save bar */
.cs-savebar {
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: min(760px, calc(100% - 32px));
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  background: #1f2937;
  color: #fff;
  border-radius: 12px;
  padding: 10px 12px 10px 18px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.25);
}
.cs-savebar-text {
  display: flex;
  align-items: center;
  gap: 8px;
}
.cs-savebar-errors {
  color: #fca5a5;
}
.cs-savebar-actions {
  display: flex;
  gap: 8px;
}
.cs-savebar .btn-outline-secondary {
  color: #e5e7eb;
  border-color: #4b5563;
  background: transparent;
}
.cs-slide-enter-active,
.cs-slide-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.cs-slide-enter-from,
.cs-slide-leave-to {
  opacity: 0;
  transform: translate(-50%, 20px);
}

@media (max-width: 991px) {
  .cs-tabs .ant-tabs-nav {
    min-width: 0;
  }
  .cs-tabs .ant-tabs-content-holder {
    padding: 16px;
  }
}
</style>
